import { ReactElement, useCallback, useContext, useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';

import { Page, CrudMode } from '@ohpen/enso-ui';
import { BrokersList, BrokersCrudForm, BROKERS_VALUE_LIST_NAMES, ConfirmCrDeleteModal, ChangeLogDrawer } from '@pmp/components';
import { ActionEntityType, IntermediaryGetAllResponse, intermediaryValidateDelete, intermediaryCreateDeleteCr } from '@pmp/api';
import { useValueListsStore } from '@pmp/stores';
import { PmpConfigContext } from '@pmp/contexts';

import { initialState, reducer } from './reducer';
import { usePmpFetch } from '@pmp/hooks';

export const BrokersPage = (): ReactElement => {
  const [isFetching, , data, , fetchData] = usePmpFetch('intermediaryGetAll');
  const [state, dispatch] = useReducer(reducer, initialState);
  const { valueLists, getValueLists } = useValueListsStore();
  const { brokerPortalUrl } = useContext(PmpConfigContext);
  const { t } = useTranslation();

  useEffect(() => {
    getValueLists({ fetchIfMissing: BROKERS_VALUE_LIST_NAMES });
  }, [getValueLists]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onCreate = useCallback(() => {
    dispatch({ type: 'openCrud', record: null, mode: CrudMode.CREATE });
  }, []);

  const onView = useCallback((record: IntermediaryGetAllResponse) => {
    dispatch({ type: 'openCrud', record, mode: CrudMode.VIEW });
  }, []);

  const onEdit = useCallback((record: IntermediaryGetAllResponse) => {
    dispatch({ type: 'openCrud', record, mode: CrudMode.EDIT });
  }, []);

  const onOpenInPanel = useCallback(
    (record: IntermediaryGetAllResponse) => {
      if (!brokerPortalUrl || !record?.firmOhpenId || !record?.ohpenId) return;
      window.open(`${brokerPortalUrl}/panel/firms/${record.firmOhpenId}/users/${record.ohpenId}/edit`, '_blank');
    },
    [brokerPortalUrl],
  );

  const onCloseCrud = useCallback(() => {
    dispatch({ type: 'closeCrud' });
  }, []);

  const onDelete = useCallback((record: IntermediaryGetAllResponse) => {
    dispatch({ type: 'openDeleteModal', record });
  }, []);

  const onCloseDeleteModal = useCallback(() => {
    dispatch({ type: 'closeDeleteModal' });
  }, []);

  const onViewChangeLog = useCallback((record: IntermediaryGetAllResponse) => {
    dispatch({
      type: 'openChangeLog',
      changeLog: {
        target: { entityType: ActionEntityType.BROKERS, id: record.id ?? '', name: record.displayName ?? '' },
      },
    });
  }, []);

  const onCloseChangeLog = useCallback(() => {
    dispatch({ type: 'closeChangeLog' });
  }, []);

  return (
    <Page>
      <Page.Title>{t('lender.brokers.title')}</Page.Title>
      <Page.Content>
        <BrokersList
          data={data ?? []}
          isFetching={isFetching}
          valueLists={valueLists}
          onCreate={onCreate}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
          onOpenInPanel={onOpenInPanel}
          onViewChangeLog={onViewChangeLog}
        />
        {state.crud.open && state.crud.mode && (
          <BrokersCrudForm
            mode={state.crud.mode}
            record={state.crud.record}
            valueLists={valueLists}
            refreshTable={fetchData}
            onClose={onCloseCrud}
          />
        )}
        <ChangeLogDrawer target={state.changeLog.target} onClose={onCloseChangeLog} />
        <ConfirmCrDeleteModal
          open={state.deleteModal.open}
          records={state.deleteModal.record ? [state.deleteModal.record] : []}
          idKey="id"
          nameKey="displayName"
          entityLabel={t('lender.brokers.labels.broker')}
          entityLabelPlural={t('lender.brokers.labels.brokers')}
          getPrecheck={intermediaryValidateDelete}
          createDeleteChangeRequest={intermediaryCreateDeleteCr}
          refreshTable={fetchData}
          onClose={onCloseDeleteModal}
        />
      </Page.Content>
    </Page>
  );
};
